import React, { useMemo } from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import { AutoAwesome as SparkleIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { tableCompactSx, tableContainerSx } from "@/components/table/table-styles";
import { tokens } from "@/tokens";
import { formatCurrency } from "../utils";
import { useAppSelector } from "@/store";
import { useGetDashboardManufacturersQuery } from "@/services";
import { roles } from "@/roles";

interface ACEOpportunitiesSectionProps {
  existingSupplierMids?: string[];
}

const ACEOpportunitiesSection: React.FC<ACEOpportunitiesSectionProps> = ({
  existingSupplierMids = [],
}) => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);

  const isTDAdminTCD = useMemo(() => {
    return user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";
  }, [user, selectedTenant]);

  const clientId = useMemo(() => {
    if (isTDAdminTCD) return selectedTenant?._id;
    if (selectedClient?._id) return selectedClient._id;
    if (user?.userRole === roles.TDCLIENT || user?.userRole === roles.SPCLIENT) return user?._id;
    return undefined;
  }, [isTDAdminTCD, selectedTenant, selectedClient, user]);

  const { data: manufacturersData } = useGetDashboardManufacturersQuery(
    {
      dashboardVisibility: {
        clientId,
        ...(isTDAdminTCD && selectedTenant && { tenantId: selectedTenant._id }),
      },
    },
    { skip: !clientId }
  );

  const opportunities = useMemo(() => {
    const manufacturers: IVendor[] = manufacturersData?.data || [];
    // Filter manufacturers not already enrolled as first sale suppliers
    // and that have significant tariff value (potential savings)
    return manufacturers
      .filter((m) => !existingSupplierMids.includes(m.manufacturer))
      .filter((m) => (m.tariffValue || 0) > 1000)
      .sort((a, b) => (b.tariffValue || 0) - (a.tariffValue || 0))
      .slice(0, 10)
      .map((m) => ({
        id: m.manufacturer,
        productName: m.manufacturerName || m.manufacturer,
        htsCode: "",
        supplierName: m.manufacturerName || m.manufacturer,
        mid: m.manufacturer,
        country: m.country || (m.manufacturer ? m.manufacturer.substring(0, 2).toUpperCase() : ""),
        estimatedSavings: Math.round((m.tariffValue || 0) * 0.15),
        entryCount: m.entryCount || 0,
        tariffValue: m.tariffValue || 0,
        reason: `${(m.entryCount || 0).toLocaleString()} entries with $${((m.tariffValue || 0) / 1000).toFixed(0)}K tariff value — potential first sale savings`,
      }));
  }, [manufacturersData, existingSupplierMids]);

  if (opportunities.length === 0) return null;

  return (
    <Box sx={{ mt: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <SparkleIcon sx={{ fontSize: 18, color: tokens.brand.accent }} />
        <Typography
          sx={{
            fontSize: tokens.fontSize.lg,
            fontWeight: tokens.fontWeight.bold,
            color: tokens.text.primary,
          }}
        >
          ACE Opportunities
        </Typography>
        <Chip
          label={`${opportunities.length} found`}
          size="small"
          sx={{
            bgcolor: tokens.chip.indigo.bg,
            color: tokens.chip.indigo.text,
            fontSize: tokens.fontSize["2xs"],
            fontWeight: tokens.fontWeight.semibold,
            height: 20,
          }}
        />
      </Stack>
      <Typography
        sx={{
          fontSize: tokens.fontSize.sm2,
          color: tokens.text.secondary,
          mb: 2,
        }}
      >
        Manufacturers identified with potential first sale savings based on import data analysis.
      </Typography>

      <Box sx={{ ...tableContainerSx, ...tableCompactSx }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Manufacturer</TableCell>
                <TableCell>MID</TableCell>
                <TableCell align="right">Tariff Value</TableCell>
                <TableCell align="right">Est. Savings</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {opportunities.map((opp) => (
                <TableRow key={opp.id}>
                  <TableCell>
                    <Typography
                      sx={{
                        fontSize: tokens.fontSize.sm2,
                        fontWeight: tokens.fontWeight.semibold,
                        color: tokens.text.primary,
                      }}
                    >
                      {opp.productName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        fontFamily: "monospace",
                        fontSize: tokens.fontSize.base,
                        color: tokens.brand.primary,
                        fontWeight: tokens.fontWeight.bold,
                      }}
                    >
                      {opp.mid}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.body }}>
                      {formatCurrency(opp.tariffValue)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      sx={{
                        fontSize: tokens.fontSize.sm2,
                        fontWeight: tokens.fontWeight.bold,
                        color: tokens.status.success,
                      }}
                    >
                      {formatCurrency(opp.estimatedSavings)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        fontSize: tokens.fontSize.xs,
                        color: tokens.text.secondary,
                        maxWidth: 280,
                      }}
                    >
                      {opp.reason}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => navigate(`/first-sale`)}
                      sx={{
                        fontSize: tokens.fontSize["2xs"],
                        fontWeight: tokens.fontWeight.semibold,
                        textTransform: "none",
                        borderColor: tokens.brand.primary,
                        color: tokens.brand.primary,
                        borderRadius: tokens.radius.sm,
                        px: 1.5,
                        py: 0.25,
                        "&:hover": {
                          bgcolor: tokens.brand.primary,
                          color: "#fff",
                          borderColor: tokens.brand.primary,
                        },
                      }}
                    >
                      Enroll
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default ACEOpportunitiesSection;
